import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { 
  BarChart3, TrendingUp, Leaf, Zap, Package, 
  CheckCircle, AlertTriangle, Recycle, Factory, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const Analytics = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch utilization data
  const { data: utilization, isLoading: loadingUtil, error: utilError } = useQuery({
    queryKey: ['analytics-utilization'],
    queryFn: async () => {
      const response = await axios.get(`${API}/analytics/utilization?days=30`, { headers });
      return response.data;
    },
    enabled: !!token,
  });

  // Fetch sustainability data
  const { data: sustainability, isLoading: loadingSust, error: sustError } = useQuery({
    queryKey: ['analytics-sustainability'],
    queryFn: async () => {
      const response = await axios.get(`${API}/analytics/sustainability?days=30`, { headers });
      return response.data;
    },
    enabled: !!token,
  });

  const handleDownloadReport = () => {
    toast.info('Downloading analytics report...');
    // Create a simple CSV export
    if (utilization && sustainability) {
      const csv = `SmartPallet Analytics Report
Generated: ${new Date().toISOString()}

UTILIZATION METRICS
Total Shipments,${utilization.total_shipments}
Completed Shipments,${utilization.completed_shipments}
Utilization Rate,${utilization.utilization_rate}%
Total Devices,${utilization.total_devices}
Active Devices,${utilization.active_devices}
Device Utilization,${utilization.device_utilization}%

SUSTAINABILITY METRICS
CO2 Saved (kg),${sustainability.co2_saved_kg}
Energy Saved (kWh),${sustainability.energy_saved_kwh}
Food Waste Prevented (kg),${sustainability.food_waste_prevented_kg}
Compliance Rate,${sustainability.compliance_rate}%
`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smartpallet_analytics_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded!');
    }
  };

  const utilizationPieData = utilization ? [
    { name: 'Active', value: utilization.active_devices },
    { name: 'Inactive', value: utilization.total_devices - utilization.active_devices },
  ] : [];

  const shipmentPieData = utilization ? [
    { name: 'Completed', value: utilization.completed_shipments },
    { name: 'In Progress', value: utilization.total_shipments - utilization.completed_shipments },
  ] : [];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Analytics & Reports</h2>
          <p className="text-slate-600 mt-1">Utilization metrics and sustainability insights</p>
        </div>
        <Button 
          className="bg-gradient-to-r from-green-600 to-emerald-500"
          onClick={handleDownloadReport}
          disabled={loadingUtil || loadingSust}
        >
          <Download className="w-4 h-4 mr-2" />
          Download Report
        </Button>
      </div>

      <Tabs defaultValue="utilization" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="utilization" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Utilization
          </TabsTrigger>
          <TabsTrigger value="sustainability" className="flex items-center gap-2">
            <Leaf className="w-4 h-4" />
            Sustainability
          </TabsTrigger>
        </TabsList>

        {/* Utilization Tab */}
        <TabsContent value="utilization" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Shipments</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {utilization?.total_shipments || 0}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Last 30 days</p>
                  </div>
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Package className="w-7 h-7 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Completed</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {utilization?.completed_shipments || 0}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {utilization?.utilization_rate || 0}% completion rate
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-7 h-7 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Active Devices</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {utilization?.active_devices || 0}/{utilization?.total_devices || 0}
                    </p>
                    <p className="text-xs text-purple-600 mt-1">
                      {utilization?.device_utilization || 0}% utilization
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Zap className="w-7 h-7 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Avg Daily</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {utilization?.total_shipments ? Math.round(utilization.total_shipments / 30) : 0}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Shipments/day</p>
                  </div>
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Shipments Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Daily Shipments (30 Days)</CardTitle>
                <CardDescription>Shipment volume over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={utilization?.daily_data || []}>
                      <defs>
                        <linearGradient id="shipmentGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="shipments" 
                        stroke="#3b82f6" 
                        fill="url(#shipmentGradient)" 
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Device Utilization Pie */}
            <Card>
              <CardHeader>
                <CardTitle>Device Utilization</CardTitle>
                <CardDescription>Active vs inactive devices</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={utilizationPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {utilizationPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-sm text-slate-600">Active</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-sm text-slate-600">Inactive</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sustainability Tab */}
        <TabsContent value="sustainability" className="space-y-6">
          {/* Sustainability Score */}
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-green-800">Sustainability Score</h3>
                  <p className="text-sm text-green-600 mt-1">Based on compliance and efficiency metrics</p>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold text-green-600">
                    {sustainability?.sustainability_score || 0}
                  </div>
                  <div className="text-sm text-green-700">out of 100</div>
                </div>
              </div>
              <Progress 
                value={sustainability?.sustainability_score || 0} 
                className="mt-4 h-3 bg-green-200"
              />
            </CardContent>
          </Card>

          {/* Impact Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Card className="border-t-4 border-t-green-500">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Recycle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  {sustainability?.co2_saved_kg || 0} kg
                </p>
                <p className="text-sm text-slate-600 mt-1">CO₂ Emissions Reduced</p>
                <p className="text-xs text-green-600 mt-2">
                  ≈ {sustainability?.metrics?.emissions_reduction}
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-orange-500">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Factory className="w-8 h-8 text-orange-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  {sustainability?.food_waste_prevented_kg || 0} kg
                </p>
                <p className="text-sm text-slate-600 mt-1">Food Waste Prevented</p>
                <p className="text-xs text-orange-600 mt-2">
                  Through temperature compliance
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-blue-500">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  {sustainability?.energy_saved_kwh || 0} kWh
                </p>
                <p className="text-sm text-slate-600 mt-1">Energy Saved</p>
                <p className="text-xs text-blue-600 mt-2">
                  Optimized cold chain efficiency
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Compliance Rate */}
          <Card>
            <CardHeader>
              <CardTitle>Temperature Compliance Rate</CardTitle>
              <CardDescription>Percentage of shipments within temperature limits</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="flex-1">
                  <Progress 
                    value={sustainability?.compliance_rate || 0} 
                    className="h-4"
                  />
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {sustainability?.compliance_rate || 0}%
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Compliant Shipments</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600 mt-2">
                    {Math.round((sustainability?.total_shipments || 0) * (sustainability?.compliance_rate || 0) / 100)}
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">Violations</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600 mt-2">
                    {(sustainability?.total_shipments || 0) - Math.round((sustainability?.total_shipments || 0) * (sustainability?.compliance_rate || 0) / 100)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;
